/**
 * TorScreen — "Identità Tor-only" (Premium). La feature NON è un interruttore
 * magico: la protezione IP vera è una DISCIPLINA — un'identità usata solo dentro
 * Tor, dalla prima volta e mai senza. Qui: la regola + il verificatore "sei su
 * Tor?" (callable checkTor) + la guida per mettersi su Tor.
 * Onesto: Tor nasconde l'IP, non rende anonimi al 100% (restano metadati).
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { checkTor, torPlatform, TOR_LINKS } from '../lib/tor';
import { isNativeApp } from '../lib/platform';
import { torStart, torStop, onTorStatus, onTorProxy, torGetStatus, exitRelayMode } from '../lib/torNative';
import { creaUsaeGetta, abortUsaeGetta, distruggiUsaeGetta, currentDisposable } from '../lib/disposableIdentity';

export default function TorScreen() {
  const navigate = useNavigate();
  const { premium, isFounder, premiumLifetime } = useStore();
  // Tor "un tocco" nel guscio nativo è una funzione CORE (non Premium): nell'app
  // Android è sempre disponibile. Sul web resta legata al Premium (là serve Orbot).
  const hasPremium = premium || isFounder || premiumLifetime || isNativeApp();
  const plat = torPlatform();
  const [tor, setTor] = useState(undefined); // undefined=loading · true/false · null=sconosciuto
  const [checking, setChecking] = useState(false);

  const native = isNativeApp();
  const [torState, setTorState] = useState('OFF'); // OFF | STARTING | ON
  const [proxyOn, setProxyOn] = useState(false); // il proxy WebView è DAVVERO agganciato?
  const [torBusy, setTorBusy] = useState(false);
  const [torErr, setTorErr] = useState(null);
  const [confirmStart, setConfirmStart] = useState(false); // avviso "ricarica + rientro"
  const [log, setLog] = useState([]);
  const t0 = useRef(0);
  const addLog = (m) => {
    const dt = t0.current ? `${((Date.now() - t0.current) / 1000).toFixed(1)}s` : '·';
    try { console.log('[TORDIAG]', `[${dt}]`, m); } catch { /* no-op */ }
    setLog((L) => [`[${dt}] ${m}`, ...L].slice(0, 40));
  };

  async function refresh() {
    // Se il proxy nativo è agganciato, LO SAPPIAMO già: salta la callable checkTor
    // (che via Tor prende un CORS da Google e darebbe un falso "sconosciuto").
    if (native && proxyOn) {
      setTor(true); setChecking(false);
      addLog('check: proxy Tor attivo → ✅ (verifica server non necessaria)');
      return;
    }
    setChecking(true);
    const r = await checkTor();
    setTor(r);
    if (native) addLog(`check "sei su Tor?": ${r === true ? '✅ SÌ' : r === false ? '⚪ no' : '⚠ sconosciuto'}`);
    setChecking(false);
  }
  useEffect(() => { refresh(); }, []);

  // Nativo: ascolta lo stato del motore Tor + l'evento proxy (per la diagnostica).
  useEffect(() => {
    if (!native) return undefined;
    let u1; let u2;
    // Dopo un reload (ingresso in modalità Tor) il motore Tor è ancora ON lato
    // nativo ma la UI riparte da zero: interroghiamo lo stato reale.
    torGetStatus().then((s) => {
      if (s?.status) { setTorState(s.status); setProxyOn(!!s.proxyApplied); }
    }).catch(() => {});
    onTorStatus((s) => {
      setTorState(s?.status || 'OFF');
      setProxyOn(!!s?.proxyApplied);
      addLog(`stato Tor: ${s?.status || '?'}`
        + (s?.httpPort ? ` · porta ${s.httpPort}` : ' · porta -')
        + (s?.proxyApplied ? ' · proxy ON' : ''));
    }).then((u) => { u1 = u; }).catch(() => {});
    onTorProxy((e) => {
      if (e && e.proxy === true) { setProxyOn(true); addLog(`✅ PROXY AGGANCIATO (porta ${e.port})`); }
      else if (e && e.failed) {
        setProxyOn(false);
        addLog('⚠ Tor connesso ma NON sono riuscito ad agganciare il traffico');
        setTorErr('Tor è partito ma non sono riuscito a instradarci il traffico. La tua connessione è normale (nessun rischio). Riprova, oppure usa Orbot (guida sotto).');
      }
      else if (e && e.proxy === false) { setProxyOn(false); addLog('proxy staccato'); }
      else if (e && typeof e.proxySupported === 'boolean') addLog(`proxy WebView supportato: ${e.proxySupported ? 'sì' : 'NO'}`);
    }).then((u) => { u2 = u; }).catch(() => {});
    return () => { try { u1?.(); } catch {} try { u2?.(); } catch {} };
  }, [native]);

  // GIVE-UP INFORMATIVO (non anti-lockout): col proxy WebView la rete NON viene
  // MAI tagliata (il proxy si aggancia solo a Tor ON), quindi restare in STARTING
  // è innocuo. Il timer serve solo a non lasciare lo spinner all'infinito. 120s
  // perché il PRIMO bootstrap di Tor su rete lenta/filtrata (download del
  // consensus) supera spesso i 45s: spegnere prima ucciderebbe un avvio legittimo.
  useEffect(() => {
    if (!native || torState !== 'STARTING') return undefined;
    const t = setTimeout(async () => {
      addLog('⏱ 2 min: Tor non è arrivato a ON — smetto di attendere');
      try { await torStop(); } catch { /* no-op */ }
      abortUsaeGetta(); // Tor mai ON → nessun reload → resti sul tuo account
      setTorState('OFF');
      setTorErr('Tor non si è connesso in 2 minuti (rete lenta o Tor filtrato qui). La tua connessione è rimasta attiva tutto il tempo — riprova, o usa Orbot (guida sotto).');
    }, 120000);
    return () => clearTimeout(t);
  }, [native, torState]);

  // USA-E-GETTA ("Massima Sicurezza"): riprogettata ISOLATA dal sistema-identità
  // (non tocca identità-attiva/catalogo/PIN → non può danneggiare il tuo account)
  // + signOut pre-auth per l'`internal`. Se fallisce, resti pulito sul tuo account.
  const DISPOSABLE_ENABLED = true;

  async function toggleTor() {
    setTorBusy(true);
    setTorErr(null);
    setProxyOn(false);
    const starting = !(torState === 'ON' || torState === 'STARTING');
    if (starting) { t0.current = Date.now(); setLog([]); }
    else addLog('⏹ stop Tor');
    try {
      if (starting) {
        if (DISPOSABLE_ENABLED) {
          addLog('🎭 creo l\'identità usa-e-getta…');
          try { const d = await creaUsaeGetta(); addLog('usa-e-getta pronta: @' + d.sigil); }
          catch (e) { addLog('⚠ usa-e-getta fallita: ' + (e?.message || e)); setTorErr('Creazione usa-e-getta fallita: sei ancora sul tuo account.'); setTorBusy(false); return; }
        }
        addLog('▶ avvio Tor…');
        const r = await torStart(addLog);
        if (!r?.ok) {
          addLog('⚠ esito: NON riuscito (' + (r?.reason || '?') + ')');
          if (DISPOSABLE_ENABLED) abortUsaeGetta();
        } else addLog('esito: comando accettato — attendo lo stato di Tor…');
      } else {
        if (DISPOSABLE_ENABLED && currentDisposable()) { try { await distruggiUsaeGetta(); } catch { /* no-op */ } }
        await torStop();
        exitRelayMode(); // torna a Firebase diretto (reload) se eravamo in modalità Tor
      }
    } catch (e) { addLog('errore start/stop: ' + (e?.message || e)); }
    finally { setTorBusy(false); setTimeout(refresh, 2500); }
  }

  if (!hasPremium) {
    return (
      <Frame statusLeft="TOR">
        <Header title="Identità Tor-only" crumb="◀" onCrumbClick={() => navigate('/impostazioni')} />
        <Box variant="warn">
          <p className="text-[15px]">🧅 <b>Identità Tor-only</b> è una funzione <b>Premium</b>.</p>
          <p className="text-[13px] text-ink-dim mt-2">
            Nascondi anche <i>da dove</i> ti colleghi: un'identità usata <b>solo</b> dentro Tor, così il tuo indirizzo non risulta mai.
          </p>
        </Box>
        <Btn variant="amber" className="mt-3" onClick={() => navigate('/premium')}>Passa a Premium</Btn>
        <Footer><Btn variant="ghost" onClick={() => navigate('/impostazioni')}>◀ Impostazioni</Btn></Footer>
      </Frame>
    );
  }

  // Nel guscio nativo, se il proxy Tor è AGGANCIATO lo sappiamo per certo (ce lo
  // dice il nativo): è la verità più affidabile. Non dipendiamo dalla callable
  // `checkTor` che, passando DA Tor, riceve un blocco CORS da Google (l'exit node
  // Tor è trattato diversamente) e darebbe un falso "sconosciuto".
  const onTor = native && proxyOn ? true : tor;
  const statusLabel = (native && proxyOn) ? '✅ Sei su Tor (proxy Nullum attivo)'
    : checking || onTor === undefined ? '… verifica in corso'
      : onTor === true ? '✅ Sei connesso via Tor'
        : onTor === false ? '⚪ NON stai usando Tor'
          : '⚠️ stato sconosciuto';

  return (
    <Frame statusLeft="TOR">
      <Header title="Identità Tor-only" crumb="◀" onCrumbClick={() => navigate('/impostazioni')} />

      {/* Nativo: IP anonimo con UN TOCCO — motore Tor incorporato nell'app */}
      {native && (
        <Box variant={torState === 'ON' && proxyOn ? 'success' : 'warn'} className="mb-1">
          <p className="text-[15px] text-ink">🧅 IP anonimo con un tocco</p>
          <p className="text-[13px] text-ink-dim mt-1">
            {torState === 'ON' && proxyOn ? 'Attivo. Il tuo indirizzo è nascosto ai server.'
              : torState === 'ON' ? 'Tor è connesso: aggancio il traffico… un istante.'
              : torState === 'STARTING' ? 'Mi connetto a Tor… ci vogliono 10–30 secondi.'
              : 'Nascondi da dove ti colleghi. Un tocco, senza altre app.'}
          </p>
          {torErr && <p className="text-[12px] text-amber mt-1">{torErr}</p>}
          <Btn
            variant={torState === 'ON' ? 'ghost' : 'amber'}
            className="mt-2"
            disabled={torBusy || torState === 'STARTING'}
            onClick={() => {
              const starting = !(torState === 'ON' || torState === 'STARTING');
              // Primo tap su "Attiva": mostra l'avviso (ricarica + rientro) e ferma.
              if (starting && !confirmStart) { setConfirmStart(true); return; }
              setConfirmStart(false);
              toggleTor();
            }}
          >
            {torBusy ? '…'
              : torState === 'ON' ? '⏹ Disattiva Tor'
                : torState === 'STARTING' ? '… connessione (max 2 min)'
                  : '🧅 Attiva Tor'}
          </Btn>

          {/* Avviso PRIMA di attivare: l'accensione ricarica l'app e chiede il rientro. */}
          {confirmStart && torState !== 'ON' && torState !== 'STARTING' && (
            <Box variant="warn" className="mt-2">
              <p className="text-[13px] text-ink">Un attimo, prima di attivare</p>
              <p className="text-[12px] text-ink-dim mt-1">
                Quando accendi Tor, l'app <b className="text-ink">si ricarica e ti chiede di rientrare</b>
                {' '}(sigil + PIN). È normale e dura pochi secondi.
              </p>
              <div className="space-y-2 mt-2">
                <Btn variant="amber" onClick={() => { setConfirmStart(false); toggleTor(); }}>Ho capito → attiva Tor</Btn>
                <Btn variant="ghost" onClick={() => setConfirmStart(false)}>Annulla</Btn>
              </div>
            </Box>
          )}

          <p className="text-[11px] text-ink-dim mt-2">
            <b className="text-ink">Non può bloccarti</b>: se Tor non risponde, si spegne da solo e torna tutto come prima. (Tor rallenta un po'.)
          </p>
        </Box>
      )}

      {/* DIAGNOSTICA: la sequenza reale (stato Tor, porta, proxy, check). Se non
          funziona, l'utente me la legge e capisco subito dove si rompe. */}
      {native && log.length > 0 && (
        <Box className="mt-1 mb-1">
          <p className="text-[13px] text-ink">🔎 Diagnostica Tor <span className="text-ink-dim text-[11px]">— se non va, leggimi queste righe</span></p>
          <div className="mt-1 max-h-44 overflow-y-auto text-[11px] font-mono text-ink-dim space-y-0.5">
            {log.map((l, i) => (
              <div key={i} className={l.includes('✅') || l.includes('AGGANCIATO') ? 'text-ink'
                : (l.includes('errore') || l.includes('timeout')) ? 'text-amber' : ''}>{l}</div>
            ))}
          </div>
          <button type="button" onClick={() => setLog([])} className="text-[11px] text-ink-dim underline mt-1">pulisci</button>
        </Box>
      )}

      {/* La regola: è qui che sta la sostanza */}
      <Box variant="warn">
        <p className="text-[14px] text-ink">La regola che rende Tor utile</p>
        <p className="text-[13px] text-ink-dim mt-1">
          Basta un sigil aperto <b>una sola volta senza Tor</b> e il tuo indirizzo resta legato a quell'identità: accendere Tor dopo non lo cancella. Per nasconderti davvero, usa un'<b>identità nuova, aperta solo dentro Tor, dalla prima volta e mai senza</b>.
        </p>
      </Box>

      {/* Come si fa */}
      <Box className="mt-3">
        <p className="text-[14px] text-ink">Come si fa</p>
        <div className="text-[13px] text-ink-dim mt-1 space-y-1">
          <p>1. Mettiti su Tor (sotto) e controlla che il check qui sia ✅.</p>
          <p>2. Solo allora crea un <b>sigil dedicato</b>.</p>
          <p>3. Apri quel sigil <b>solo</b> con Tor attivo. Mai senza.</p>
        </div>
      </Box>

      {/* Verificatore: il pezzo operativo prezioso */}
      <Box variant={onTor === true ? 'success' : 'default'} className="mt-3">
        <p className="text-[16px]">Sei su Tor adesso? {statusLabel}</p>
        <button
          type="button"
          onClick={refresh}
          disabled={checking}
          className="text-[12px] text-ink-dim hover:text-ink underline mt-1 disabled:opacity-40"
        >
          ricontrolla
        </button>
      </Box>

      {/* Guida Orbot: via AFFIDABILE per l'IP anonimo (fallback all'one-tap sperimentale) */}
      {(
      <Box className="mt-3">
        {plat === 'android' && (
          <div className="text-[13px] text-ink-dim space-y-1.5">
            <p className="text-ink">Mettiti su Tor — Android</p>
            <p>1. Installa <a className="text-ink underline" href={TOR_LINKS.orbotAndroid} target="_blank" rel="noopener">Orbot</a> (anche fuori dallo store).</p>
            <p>2. Apri Orbot → attiva la <b>modalità VPN</b>.</p>
            <p>3. Torna su Nullum e <b>ricarica</b>: ora passi per Tor. Le notifiche restano attive.</p>
          </div>
        )}
        {plat === 'ios' && (
          <div className="text-[13px] text-ink-dim space-y-1.5">
            <p className="text-ink">Mettiti su Tor — iPhone</p>
            <p>Consigliato: <a className="text-ink underline" href={TOR_LINKS.orbotIos} target="_blank" rel="noopener">Orbot per iOS</a> → tieni Nullum sulla Home, con le <b>notifiche</b>.</p>
            <p>Alternativa: <a className="text-ink underline" href={TOR_LINKS.onionBrowserIos} target="_blank" rel="noopener">Onion Browser</a> (browser separato: <b>perdi le notifiche</b>).</p>
          </div>
        )}
        {plat === 'desktop' && (
          <div className="text-[13px] text-ink-dim space-y-1.5">
            <p className="text-ink">Mettiti su Tor — Desktop</p>
            <p>Apri Nullum dentro <a className="text-ink underline" href={TOR_LINKS.torBrowserDesktop} target="_blank" rel="noopener">Tor Browser</a>.</p>
          </div>
        )}
      </Box>
      )}

      {/* La protezione più completa: Tor sommato al resto */}
      <Box variant="success" className="mt-3">
        <p className="text-[13px] text-ink">Con Tor, Nullum copre tutti i livelli insieme</p>
        <p className="text-[12px] text-ink-dim mt-1">
          Quello che scrivi resta leggibile solo a te e a chi ti scrive, la tua identità non è un dato reale, chi ti scrive è nascosto al server — e con Tor sparisce <b className="text-ink">anche da dove ti colleghi</b>. È la protezione più completa di Nullum. (Tor rallenta un po'.)
        </p>
      </Box>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/impostazioni')}>◀ Impostazioni</Btn>
      </Footer>
    </Frame>
  );
}
